/**
 * Configurator Redux Toolkit slice.
 *
 * State shape covers wizard navigation (`currentStep`, `completedSteps`),
 * the server-loaded configurator config (`config`, `configStatus`,
 * `configError`), and all per-step selection fields.
 *
 * Thunk: `fetchConfiguratorConfig` calls the BACKED
 * `GET /api/v1/product-types/:type/configurator` via the SF-CONFIG-002
 * API client. Uses `rejectWithValue` to surface structured API errors
 * into `configError`.
 *
 * Navigation actions (`advance`, `goBack`, `jumpToStep`) are consumed by
 * `useConfiguratorNavigation`. Individual setter actions allow step
 * components to update exactly one selection field without cascading.
 *
 * Downstream cascade map: when jumping back to a step the cascade resets
 * all selections that depend on that step. Step names are resolved to
 * indices from `config.stepSequence` at dispatch time.
 *
 * SF-CONFIG-001.
 */
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { fetchConfiguratorConfig as apiFetchConfiguratorConfig } from '@dfq/shared/api/configurator';
import type { ConfiguratorConfigDTO } from '@dfq/shared/types/configurator';

// ============================================================================
// Types
// ============================================================================

/** Price breakdown written by step components; the slice only stores it. */
export interface PriceBreakdown {
  readonly basePrice: number;
  readonly materialModifier: number;
  readonly finishingModifier: number;
  readonly quantityUnitPrice: number;
  readonly totalPrice: number;
  readonly quantity: number;
}

/** Map of option-group ID → array of selected option-value IDs. */
export type FinishingOptionsMap = Record<number, number[]>;

// --------------------------------------------------------------------------
// ArtworkData discriminated union — keyed by artworkPath value
// --------------------------------------------------------------------------

/** Path A — auto-contour job. The contour job ID is the reference. */
export interface ContourArtworkData {
  readonly artworkPath: 'A';
  readonly contourJobId: number;
}

/** Path B — customer-drawn custom shape. */
export interface CustomShapeArtworkData {
  readonly artworkPath: 'B';
  /**
   * Server-assigned id after `POST /custom-shapes` succeeds.
   * `null` when the customer is anonymous — the shape row is created at
   * checkout after account creation (#1199).
   */
  readonly customShapeId: number | null;
  /**
   * Raw Paper.js exportJSON string, carried client-side for anonymous
   * users until the shape row is persisted at checkout. Null once
   * `customShapeId` is set (authenticated flow).
   */
  readonly pendingPathDataJson?: string | null;
  /**
   * Server-assigned artwork file id from `uploadArtworkFile`, present when
   * the authenticated user uploaded artwork alongside the traced cut path.
   */
  readonly artworkFileId?: number | null;
  /**
   * IndexedDB draft id from `artworkDraftStore.putDraft`, carried for
   * anonymous users so the artwork file survives until checkout.
   */
  readonly pendingArtworkDraftId?: string | null;
  /**
   * Client-side PNG data URL of the artwork clipped to the traced path.
   * Generated at confirm time in the browser; never uploaded. Used only
   * to render the post-confirm die-cut preview in the left pane.
   */
  readonly customShapePreviewUrl?: string | null;
  /**
   * Border + bounding metadata captured alongside `pendingPathDataJson`,
   * mirroring the authenticated `saveCustomShape` payload fields. Carried
   * so an anonymous guest's shape can be materialized server-side (as
   * `pending_shape_path_json` on add-to-cart) after registration (#1199).
   */
  readonly pendingShapeSource?: string | null;
  readonly pendingBorderThicknessInches?: number | null;
  readonly pendingBorderColorHex?: string | null;
  readonly pendingBoundingWidthInches?: number | null;
  readonly pendingBoundingHeightInches?: number | null;
}

/** Path C — online designer session. */
export interface DesignArtworkData {
  readonly artworkPath: 'C';
  readonly customerDesignId: number;
}

/** Path D — design-service brief. The brief reference is a free-form string. */
export interface DesignServiceArtworkData {
  readonly artworkPath: 'D';
  readonly briefReference: string;
}

/** Discriminated union for the `artworkData` state field. */
export type ArtworkData =
  | ContourArtworkData
  | CustomShapeArtworkData
  | DesignArtworkData
  | DesignServiceArtworkData;

/** `artworkPath` — which of the four artwork-capture paths the customer chose. */
export type ArtworkPath = 'A' | 'B' | 'C' | 'D';

// --------------------------------------------------------------------------
// Status
// --------------------------------------------------------------------------

export type ConfigStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// --------------------------------------------------------------------------
// State
// --------------------------------------------------------------------------

export interface ConfiguratorState {
  /** Current step index (0-based). */
  currentStep: number;
  /** Indices of steps the customer has confirmed (marked complete). */
  completedSteps: number[];
  /** Server-loaded configurator configuration. Null until loaded. */
  config: ConfiguratorConfigDTO | null;
  configStatus: ConfigStatus;
  configError: string | null;
  /** Request id of the latest config fetch; responses from any other request are dropped. */
  configRequestId: string | null;
  // Per-step selection fields
  selectedShape: number | null;
  selectedSize: number | null;
  /** True when the customer picked the "custom size" tile on the size step, rather than a preset. */
  isCustomSize: boolean;
  customSizeWidthIn: number | null;
  customSizeHeightIn: number | null;
  selectedMaterial: number | null;
  finishingOptions: FinishingOptionsMap;
  quantity: number | null;
  artworkPath: ArtworkPath | null;
  artworkData: ArtworkData | null;
  proofType: string | null;
  priceBreakdown: PriceBreakdown | null;
}

const initialState: ConfiguratorState = {
  currentStep: 0,
  completedSteps: [],
  config: null,
  configStatus: 'idle',
  configError: null,
  configRequestId: null,
  selectedShape: null,
  selectedSize: null,
  isCustomSize: false,
  customSizeWidthIn: null,
  customSizeHeightIn: null,
  selectedMaterial: null,
  finishingOptions: {},
  quantity: null,
  artworkPath: null,
  artworkData: null,
  proofType: null,
  priceBreakdown: null,
};

// ============================================================================
// Cascade-reset map
//
// Keyed by step name. Each entry lists the state fields to clear when the
// customer jumps back to that step. Resolved to an index at dispatch time
// via `config.stepSequence`.
//
// Invariant: each step clears all fields downstream of itself.
// ============================================================================

type SelectionField =
  | 'selectedShape'
  | 'selectedSize'
  | 'isCustomSize'
  | 'customSizeWidthIn'
  | 'customSizeHeightIn'
  | 'selectedMaterial'
  | 'finishingOptions'
  | 'quantity'
  | 'artworkPath'
  | 'artworkData'
  | 'proofType';

const SELECTION_FIELD_DEFAULTS: Record<SelectionField, ConfiguratorState[SelectionField]> = {
  selectedShape: null,
  selectedSize: null,
  isCustomSize: false,
  customSizeWidthIn: null,
  customSizeHeightIn: null,
  selectedMaterial: null,
  finishingOptions: {},
  quantity: null,
  artworkPath: null,
  artworkData: null,
  proofType: null,
};

/**
 * Step-name → fields to clear when jumping back to that step.
 *
 * A jump to step N means the customer is re-making the choice at N, so
 * everything downstream of N must be invalidated.
 */
export const CASCADE_RESET_MAP: Readonly<Record<string, SelectionField[]>> = {
  shape: ['selectedSize', 'isCustomSize', 'customSizeWidthIn', 'customSizeHeightIn', 'selectedMaterial', 'finishingOptions', 'quantity', 'artworkPath', 'artworkData', 'proofType'],
  size: ['selectedMaterial', 'finishingOptions', 'quantity', 'artworkPath', 'artworkData', 'proofType'],
  material: ['finishingOptions', 'quantity', 'artworkPath', 'artworkData', 'proofType'],
  finishing: ['quantity', 'artworkPath', 'artworkData', 'proofType'],
  quantity: ['artworkPath', 'artworkData', 'proofType'],
  // artwork / proof / cart have no downstream fields to clear in the map
};

// ============================================================================
// Thunk
// ============================================================================

/**
 * Loads the step-by-step configurator config for a product type slug.
 * Calls `GET /api/v1/product-types/:type/configurator` via the SF-CONFIG-002
 * API client. Uses `rejectWithValue` to surface structured API errors into
 * `configError` rather than letting the thunk error bubble as a plain string.
 */
export const fetchConfiguratorConfig = createAsyncThunk<
  ConfiguratorConfigDTO,
  string,
  { rejectValue: string }
>('configurator/fetchConfig', async (productType, { rejectWithValue }) => {
  try {
    return await apiFetchConfiguratorConfig(productType);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to load configurator config';
    return rejectWithValue(message);
  }
});

// ============================================================================
// Slice
// ============================================================================

const configuratorSlice = createSlice({
  name: 'configurator',
  initialState,
  reducers: {
    /**
     * Increment `currentStep` by one and mark the previous step complete.
     * No-op when already at the final step.
     */
    advance(state) {
      const totalSteps = state.config?.stepSequence.length ?? 0;
      if (totalSteps > 0 && state.currentStep >= totalSteps - 1) return;
      const prev = state.currentStep;
      state.currentStep += 1;
      if (!state.completedSteps.includes(prev)) {
        state.completedSteps.push(prev);
      }
    },

    /**
     * Decrement `currentStep` by one. No-op at step 0.
     * Does NOT modify `completedSteps` or any selection field.
     */
    goBack(state) {
      if (state.currentStep === 0) return;
      state.currentStep -= 1;
    },

    /**
     * Navigate to an already-completed step and execute the downstream
     * cascade reset. Rejects (no-op) if the target step is not in
     * `completedSteps`.
     */
    jumpToStep(state, action: PayloadAction<number>) {
      const target = action.payload;
      if (!state.completedSteps.includes(target)) return;

      // Resolve which step name the target index maps to.
      const stepName = state.config?.stepSequence[target];
      if (stepName !== undefined) {
        const fieldsToClear = CASCADE_RESET_MAP[stepName];
        if (fieldsToClear !== undefined) {
          for (const field of fieldsToClear) {
            // Casting through unknown is necessary because different fields have
            // different value types; the defaults map guarantees type-correctness.
            (state[field] as unknown) = SELECTION_FIELD_DEFAULTS[field];
          }
        }
      }

      state.currentStep = target;
    },

    /** Reset all slice state to initial values. */
    resetConfigurator() {
      return initialState;
    },

    // ------------------------------------------------------------------
    // Individual selection setters — no cascade side-effects
    // ------------------------------------------------------------------

    setSelectedShape(state, action: PayloadAction<number | null>) {
      state.selectedShape = action.payload;
    },
    /** Selects a predefined size tile — clears `isCustomSize` since the two are mutually exclusive. */
    setSelectedSize(state, action: PayloadAction<number | null>) {
      state.selectedSize = action.payload;
      state.isCustomSize = false;
    },
    /** Selects the "custom size" tile — clears `selectedSize` since the two are mutually exclusive. */
    setIsCustomSize(state, action: PayloadAction<boolean>) {
      state.isCustomSize = action.payload;
      if (action.payload) state.selectedSize = null;
    },
    setCustomSizeWidthIn(state, action: PayloadAction<number | null>) {
      state.customSizeWidthIn = action.payload;
    },
    setCustomSizeHeightIn(state, action: PayloadAction<number | null>) {
      state.customSizeHeightIn = action.payload;
    },
    setSelectedMaterial(state, action: PayloadAction<number | null>) {
      state.selectedMaterial = action.payload;
    },
    setFinishingOptions(state, action: PayloadAction<FinishingOptionsMap>) {
      state.finishingOptions = action.payload;
    },
    setQuantity(state, action: PayloadAction<number | null>) {
      state.quantity = action.payload;
    },
    setArtworkPath(state, action: PayloadAction<ArtworkPath | null>) {
      state.artworkPath = action.payload;
    },
    setArtworkData(state, action: PayloadAction<ArtworkData | null>) {
      state.artworkData = action.payload;
    },
    setProofType(state, action: PayloadAction<string | null>) {
      state.proofType = action.payload;
    },
    setPriceBreakdown(state, action: PayloadAction<PriceBreakdown | null>) {
      state.priceBreakdown = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfiguratorConfig.pending, (state, action) => {
        state.configRequestId = action.meta.requestId;
        state.configStatus = 'loading';
        state.configError = null;
      })
      .addCase(fetchConfiguratorConfig.fulfilled, (state, action) => {
        if (state.configRequestId !== null && action.meta?.requestId !== state.configRequestId) return;
        state.configStatus = 'succeeded';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.config = action.payload as any;
      })
      .addCase(fetchConfiguratorConfig.rejected, (state, action) => {
        if (state.configRequestId !== null && action.meta?.requestId !== state.configRequestId) return;
        state.configStatus = 'failed';
        state.configError = action.payload ?? action.error.message ?? 'Unknown error';
      });
  },
});

export const {
  advance,
  goBack,
  jumpToStep,
  resetConfigurator,
  setSelectedShape,
  setSelectedSize,
  setIsCustomSize,
  setCustomSizeWidthIn,
  setCustomSizeHeightIn,
  setSelectedMaterial,
  setFinishingOptions,
  setQuantity,
  setArtworkPath,
  setArtworkData,
  setProofType,
  setPriceBreakdown,
} = configuratorSlice.actions;

export const configuratorReducer = configuratorSlice.reducer;

// ============================================================================
// Selectors
// ============================================================================

interface RootSliceState {
  configurator: ConfiguratorState;
}

const selectConfiguratorState = (state: RootSliceState): ConfiguratorState =>
  state.configurator;

export const selectCurrentStep = createSelector(
  [selectConfiguratorState],
  (s): number => s.currentStep,
);

export const selectCompletedSteps = createSelector(
  [selectConfiguratorState],
  (s): number[] => s.completedSteps,
);

export const selectConfig = createSelector(
  [selectConfiguratorState],
  (s): ConfiguratorConfigDTO | null => s.config,
);

export const selectConfigStatus = createSelector(
  [selectConfiguratorState],
  (s): ConfigStatus => s.configStatus,
);

export const selectConfigError = createSelector(
  [selectConfiguratorState],
  (s): string | null => s.configError,
);

export const selectSelectedShape = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.selectedShape,
);

export const selectSelectedSize = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.selectedSize,
);

export const selectIsCustomSize = createSelector(
  [selectConfiguratorState],
  (s): boolean => s.isCustomSize,
);

export const selectCustomSizeWidthIn = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.customSizeWidthIn,
);

export const selectCustomSizeHeightIn = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.customSizeHeightIn,
);

export const selectSelectedMaterial = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.selectedMaterial,
);

export const selectFinishingOptions = createSelector(
  [selectConfiguratorState],
  (s): FinishingOptionsMap => s.finishingOptions,
);

export const selectQuantity = createSelector(
  [selectConfiguratorState],
  (s): number | null => s.quantity,
);

export const selectArtworkPath = createSelector(
  [selectConfiguratorState],
  (s): ArtworkPath | null => s.artworkPath,
);

export const selectArtworkData = createSelector(
  [selectConfiguratorState],
  (s): ArtworkData | null => s.artworkData,
);

export const selectCustomShapePreviewUrl = createSelector(
  [selectArtworkData],
  (data): string | null =>
    data?.artworkPath === 'B' ? (data.customShapePreviewUrl ?? null) : null,
);

export const selectProofType = createSelector(
  [selectConfiguratorState],
  (s): string | null => s.proofType,
);

export const selectPriceBreakdown = createSelector(
  [selectConfiguratorState],
  (s): PriceBreakdown | null => s.priceBreakdown,
);

/**
 * Returns `true` when the given step index is in `completedSteps`.
 * Used by the progress bar component.
 */
export const selectIsStepCompleted = (step: number) =>
  createSelector(
    [selectCompletedSteps],
    (completed): boolean => completed.includes(step),
  );
